import Button from '@/components/ui/button'
import Tag from '@/components/ui/tag'
import DetailSection from '@/components/companies/detail/detail-section'
import InitialsAvatar from '@/components/generals/initials-avatar'
import { stageMeta } from '@/constants/crm'
import { formatMoney } from '@/lib/companies'
import { formatShortDate } from '@/lib/dates'
import { useCrmStore } from '@/stores/crm-store-provider'
import PlusIcon from '@/assets/images/_common/plus.svg'

interface CompanyRelationsProps {
    companyId: string
}

export default function CompanyRelations({ companyId }: CompanyRelationsProps) {
    const allDeals = useCrmStore(state => state.deals)
    const allContacts = useCrmStore(state => state.contacts)
    const openDialog = useCrmStore(state => state.openDialog)
    const openContact = useCrmStore(state => state.openContact)
    const deals = allDeals.filter(deal => deal.companyId === companyId)
    const contacts = allContacts.filter(contact => contact.companyId === companyId)

    return (
        <>
            <DetailSection
                title="Deals"
                action={(
                    <Button variant='secondary' size='sm' onClick={() => openDialog('deal', { companyId })}>
                        <PlusIcon aria-hidden className='size-3' />
                        New Deal
                    </Button>
                )}
            >
                {deals.length > 0 ? (
                    <ul className='-mx-2 flex flex-col gap-0.5'>
                        {deals.map(deal => {
                            const stage = stageMeta(deal.stage)

                            return (
                                <li key={deal.id}>
                                    <Button
                                        variant='item'
                                        size='md'
                                        onClick={() => openDialog('deal', { dealId: deal.id })}
                                        aria-label={`Edit deal ${deal.name}`}
                                        className='items-center px-2 py-2'
                                    >
                                        <span className='flex min-w-0 flex-1 flex-col gap-1.5'>
                                            <span className='truncate' data-mdlfy-cms={`deals:${deal.id}:name`}>
                                                {deal.name}
                                            </span>
                                            <span className='caption-style text-subtle'>
                                                Closes {formatShortDate(deal.closeDate)}
                                            </span>
                                        </span>
                                        <span className='flex shrink-0 flex-col items-end gap-1.5'>
                                            <span className='flex items-center gap-1 tabular-nums'>
                                                <span className='text-muted-foreground'>$</span>
                                                <span>{formatMoney(deal.value)}</span>
                                            </span>
                                            <Tag tone={stage.tone} size='sm' className='h-[18px] text-[11px]'>
                                                {stage.value}
                                            </Tag>
                                        </span>
                                    </Button>
                                </li>
                            )
                        })}
                    </ul>
                ) : (
                    <span className='caption-style text-subtle block'>
                        No deals with this company yet.
                    </span>
                )}
            </DetailSection>

            <DetailSection
                title="Contacts"
                action={(
                    <Button variant='secondary' size='sm' onClick={() => openDialog('contact', { companyId })}>
                        <PlusIcon aria-hidden className='size-3' />
                        New Contact
                    </Button>
                )}
            >
                {contacts.length > 0 ? (
                    <ul className='-mx-2 flex flex-col gap-0.5'>
                        {contacts.map(contact => (
                            <li key={contact.id}>
                                <Button
                                    variant='item'
                                    size='md'
                                    onClick={() => openContact(contact.id)}
                                    aria-label={`Open contact ${contact.name}`}
                                    className='items-center px-2 py-2'
                                >
                                    <InitialsAvatar name={contact.name} className='size-7' />
                                    <span className='flex min-w-0 flex-1 flex-col gap-1.5'>
                                        <span className='truncate' data-mdlfy-cms={`contacts:${contact.id}:name`}>
                                            {contact.name}
                                        </span>
                                        <span className='caption-style text-subtle truncate' data-mdlfy-cms={`contacts:${contact.id}:title`}>
                                            {contact.title}
                                        </span>
                                    </span>
                                </Button>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <span className='caption-style text-subtle block'>
                        No contacts at this company yet.
                    </span>
                )}
            </DetailSection>
        </>
    )
}
